import React, { useEffect, useState } from 'react';
import { IconButton, Panel, Stack, Text, Toggle } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { INTEGRATION_LABEL_STYLE } from '../integrationLayout';
import { OrganizationDto, ZammadTicketConfigurationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { ZammadDeactivationModal } from '../Zammad/ZammadDeactivationModal';
import { resolveZammadErrorMessage } from '../Zammad/ZammadSettingsForm';
import { IZammadTicketConfiguration } from './zammadTicket.types';
import { ZammadTicketSettingsForm } from './ZammadTicketSettingsForm';

interface IZammadTicketSettingsProps {
    isZammadTicketEnabled: boolean;
    zammadTicketConfiguration: ZammadTicketConfigurationDto | null;
    updateZammadTicketConfig: (config: IZammadTicketConfiguration) => Promise<OrganizationDto>;
    isOrgLoaded: boolean;
}

// Same toggle → panel → save / confirm-to-disable flow as ZammadSettings, for the separate
// "report a ticket" integration (e-mail or Zammad web form; no Zammad account needed).
export const ZammadTicketSettings: React.FC<IZammadTicketSettingsProps> = ({
    isZammadTicketEnabled,
    zammadTicketConfiguration,
    updateZammadTicketConfig,
    isOrgLoaded
}) => {
    const [isToggleOn, setIsToggleOn] = useState(isZammadTicketEnabled);
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
    const [isDeactivationModalVisible, setIsDeactivationModalVisible] = useState(false);
    const [deactivationErrorMessage, setDeactivationErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        setIsToggleOn(isZammadTicketEnabled);
    }, [isZammadTicketEnabled]);

    const handleToggleChange = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean): void => {
        setDeactivationErrorMessage(null);
        if (checked) {
            setIsToggleOn(true);
            return;
        }
        if (isZammadTicketEnabled) {
            setIsDeactivationModalVisible(true);
        } else {
            setIsToggleOn(false);
        }
    };

    const handleConfirmDeactivate = async (): Promise<void> => {
        setIsDeactivationModalVisible(false);
        setDeactivationErrorMessage(null);
        try {
            await updateZammadTicketConfig({});
        } catch (err) {
            console.error('Failed to disable Zammad ticket reporting:', err);
            setDeactivationErrorMessage(resolveZammadErrorMessage(err));
        }
    };

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                <Stack style={INTEGRATION_LABEL_STYLE}>
                    <Text style={{ fontWeight: 600 }}>{IvicosStrings.zammadTicketSettingsToggleLabel}</Text>
                </Stack>
                <Stack.Item grow />
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Toggle
                        checked={isToggleOn}
                        onText={IvicosStrings.providerEnabledLabel}
                        offText={IvicosStrings.providerDisabledLabel}
                        disabled={!isOrgLoaded}
                        onChange={handleToggleChange}
                        styles={{ root: { minWidth: 120, marginBottom: 0 } }}
                        data-test-id="zammad-ticket-settings-toggle"
                    />
                    {isToggleOn && (
                        <>
                            <IconButton
                                iconProps={{ iconName: 'Settings' }}
                                onClick={() => setIsSettingsPanelOpen(true)}
                                data-test-id="zammad-ticket-settings-settings-icon"
                            />
                            <Text
                                style={{ color: isZammadTicketEnabled ? '#107C10' : '#A80000', fontWeight: 600 }}
                                data-test-id="zammad-ticket-settings-status"
                            >
                                {isZammadTicketEnabled ? IvicosStrings.zammadSettingsConfiguredStatus : IvicosStrings.zammadSettingsNotConfiguredStatus}
                            </Text>
                        </>
                    )}
                </Stack>
            </Stack>
            {deactivationErrorMessage && <Text style={{ color: '#A80000' }}>{deactivationErrorMessage}</Text>}
            <Panel
                headerText={IvicosStrings.zammadTicketSettingsToggleLabel}
                isOpen={isSettingsPanelOpen}
                onDismiss={() => setIsSettingsPanelOpen(false)}
                closeButtonAriaLabel="Close"
            >
                <ZammadTicketSettingsForm
                    configuration={zammadTicketConfiguration}
                    isOpen={isSettingsPanelOpen}
                    updateZammadTicketConfig={updateZammadTicketConfig}
                    onSaveSuccess={() => setIsSettingsPanelOpen(false)}
                    onCancel={() => setIsSettingsPanelOpen(false)}
                />
            </Panel>
            <ZammadDeactivationModal
                isOpen={isDeactivationModalVisible}
                onCancel={() => setIsDeactivationModalVisible(false)}
                onConfirm={handleConfirmDeactivate}
                label={IvicosStrings.zammadTicketSettingsToggleLabel}
            />
        </Stack>
    );
};
